import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { Check, Copy, Download, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { fetchJob, fetchDownload } from "@/lib/api";
import { recoveryKey, rememberJob, clearActive, forgetJob, isAbandoned, isUnpaid } from "@/lib/jobs";
import { engineLabel, useTitle } from "@/lib/brand";
import { Loading } from "@/components/Kit";
import Mark from "@/components/Mark";

/**
 * THE TRAINING JOURNEY — rebuilt 2026-10-27 as the app's stage-by-stage pane (Dog 10-08: "a list of
 * stages, current one marked, done ones ticked"). Everything comes from the hub's GET /jobs/{id}:
 * the state, one sentence, the line position, the estimate. Rules kept from 08-29 (backlog 165):
 * no silence without explanation, movement on every wait, never disclose the pipeline's insides.
 * An unpaid checkout older than two hours is said plainly — it used to read "Waiting for payment"
 * with a clock counting past 44,000 minutes.
 */
const STAGES = [
  { key: "paid", label: "Payment confirmed" },
  { key: "queued", label: "In line" },
  { key: "running", label: "Reading sources and training" },
  { key: "packaging", label: "Testing and packaging" },
  { key: "delivered", label: "Ready to download" },
];
const ORDER = { quoted: 0, awaiting_payment: 0, queued: 1, running: 2, packaging: 3, delivered: 5 };

function fmtDuration(sec) {
  if (sec == null || sec < 0) return "";
  const m = Math.floor(sec / 60);
  if (m < 1) return "under a minute";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
}

export default function ProgressPage() {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [dlMsg, setDlMsg] = useState(null);
  const [dlReady, setDlReady] = useState(false);
  const [now, setNow] = useState(Date.now());
  const asked = useRef(false);
  useTitle(job?.subject ? `Training · ${job.subject}` : "Training");

  const key = location.state?.recovery_key || recoveryKey(jobId);
  useEffect(() => { if (location.state?.recovery_key) rememberJob(jobId, location.state.recovery_key); }, [jobId, location.state]);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const j = await fetchJob(jobId);
        if (!alive) return;
        setJob(j); setError(null);
        if (["queued", "running", "packaging"].includes(j.state)) rememberJob(jobId, null);
        if (["delivered", "failed", "blocked", "refunded"].includes(j.state) || isAbandoned(j)) clearActive(jobId);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404 ? "We couldn't find this training." : "Reconnecting…");
      }
    };
    poll();
    const iv = setInterval(poll, 5000);
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { alive = false; clearInterval(iv); clearInterval(clock); };
  }, [jobId]);

  // delivered → ask the hub for a link (an evicted copy is a short wait, not a failure)
  useEffect(() => {
    if (!job || job.state !== "delivered" || asked.current) return;
    asked.current = true;
    const ask = async () => {
      try {
        const d = await fetchDownload(jobId);
        if (d.status === "ready") { setDlReady(true); setDlMsg(null); }
        else { setDlMsg(d.message || "Fetching your Expert — this takes a minute."); setTimeout(ask, (d.retry_after_seconds || 30) * 1000); }
      } catch (e) { setDlMsg(e?.response?.data?.detail || "The download link isn't ready yet."); }
    };
    ask();
  }, [job, jobId]);

  const download = async () => {
    try {
      const d = await fetchDownload(jobId);
      if (d.status === "ready" && d.url) window.location.assign(d.url);
      else setDlMsg(d.message || "Fetching your Expert — this takes a minute.");
    } catch (e) { setDlMsg(e?.response?.data?.detail || "The download link isn't ready yet."); }
  };

  const copyKey = async () => {
    try { await navigator.clipboard.writeText(key); toast.success("Recovery words copied"); } catch (_) { /* no clipboard */ }
  };

  const startedAt = job?.created_at ? Date.parse(job.created_at) : null;
  const elapsed = startedAt ? Math.floor((now - startedAt) / 1000) : null;
  const failed = ["failed", "blocked", "refunded"].includes(job?.state);
  const abandoned = isAbandoned(job);
  const unpaid = isUnpaid(job) && !abandoned;
  const done = job?.state === "delivered";
  const at = job ? (ORDER[job.state] ?? 0) : 0;

  return (
    <main data-testid="progress-page" className="ex-page">
      <div className="ex-narrow pb-28">
        <Link to="/library" className="ex-btn ex-btn-quiet ex-btn-sm -ml-4 mt-8"><ArrowLeft className="h-4 w-4" strokeWidth={1.8} /> My library</Link>
        {!job && !error && <Loading label="Connecting" />}
        {!job && error && <div className="ex-panel p-10 mt-8 text-center text-white">{error}</div>}

        {job && (
          <div className="pt-8">
            <div className="ex-eyebrow">{abandoned ? "Checkout not completed" : done ? "Your Expert is ready" : failed ? "Training stopped" : "Training"}</div>
            <h1 className="ex-h1 mt-4">{job.subject}</h1>
            <div className="ex-small mt-3">
              {[job.engine_id && `For the ${engineLabel(job.engine_id)}`, job.scope && job.scope[0].toUpperCase() + job.scope.slice(1), job.display_code].filter(Boolean).join(" · ")}
            </div>

            {abandoned ? (
              <div className="ex-panel p-7 mt-10" data-testid="abandoned">
                <div className="text-white text-[17px]">This checkout wasn't completed, so nothing was charged and nothing will be trained.</div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <button type="button" className="ex-btn ex-btn-primary ex-btn-sm" onClick={() => { forgetJob(jobId); navigate("/train", { state: { prefill: job.subject } }); }}>Train it again</button>
                  <button type="button" className="ex-btn ex-btn-secondary ex-btn-sm" onClick={() => { forgetJob(jobId); navigate("/"); }}>Remove from this browser</button>
                </div>
              </div>
            ) : (
              <>
                {key && !failed && (
                  <div className="ex-panel p-6 mt-10" data-testid="recovery-key-box">
                    <div className="flex items-center justify-between gap-4">
                      <div className="ex-eyebrow">Your seven recovery words</div>
                      <button type="button" onClick={copyKey} className="ex-btn ex-btn-quiet ex-btn-sm"><Copy className="h-4 w-4" strokeWidth={1.8} /> Copy</button>
                    </div>
                    <div data-testid="recovery-key" className="text-white text-[20px] md:text-[22px] font-medium tracking-[-0.01em] mt-3 select-all">{key}</div>
                    <p className="ex-small mt-3">They prove this Expert is yours on any phone or browser. Write them down — this browser keeps a copy under My library, and nowhere else has them.</p>
                  </div>
                )}

                {!failed && (
                  <div className="ex-panel p-7 mt-5" data-testid="stages">
                    <ol className="space-y-5">
                      {STAGES.map((s, i) => {
                        const state = done || i < at ? "done" : i === at && !unpaid ? "active" : i === 0 && unpaid ? "active" : "wait";
                        return (
                          <li key={s.key} className="flex items-start gap-4">
                            <span className={`mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border ${state === "done" ? "bg-white border-white" : state === "active" ? "border-white" : "border-[color:var(--ex-line2)]"}`}>
                              {state === "done" && <Check className="h-3.5 w-3.5 text-[color:var(--ex-bg)]" strokeWidth={2.6} />}
                              {state === "active" && <span className="ex-spinner" style={{ width: 12, height: 12 }} />}
                            </span>
                            <span className="min-w-0">
                              <span className={`block text-[16px] ${state === "wait" ? "text-[color:var(--ex-faint)]" : "text-white"}`}>
                                {i === 0 && unpaid ? "Waiting for payment" : s.label}
                              </span>
                              {state === "active" && (
                                <span className="block ex-small mt-1" data-testid="progress-line">
                                  {unpaid ? "Finish checkout in the Stripe window. Nothing is charged until it's complete." : job.progress}
                                  {job.state === "queued" && job.queue_position > 1 ? ` · ${job.queue_position - 1} ahead of you` : ""}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                    {!done && (
                      <div className="mt-7 pt-5 border-t border-[color:var(--ex-line)] flex flex-wrap gap-x-6 gap-y-1 ex-small">
                        {elapsed != null && <span>Started {fmtDuration(elapsed)} ago</span>}
                        {!unpaid && job.eta_seconds != null && <span>About {fmtDuration(job.eta_seconds)} to go</span>}
                        <span>You can close this page — training keeps going.</span>
                      </div>
                    )}
                  </div>
                )}

                {done && (
                  <div className="ex-panel p-7 mt-5 text-center" data-testid="progress-download-ready">
                    <Mark size={72} strokeBoost={2} className="mx-auto text-white" />
                    <div className="text-white text-[18px] font-medium mt-5">Your Expert is ready.</div>
                    {job.objects ? <div className="ex-small mt-1">{Number(job.objects).toLocaleString()} facts inside</div> : null}
                    <button type="button" onClick={download} disabled={!dlReady} className="ex-btn ex-btn-primary mt-6" data-testid="progress-download-btn">
                      <Download className="h-4 w-4" strokeWidth={2} /> {dlReady ? "Download your Expert" : "Preparing the link…"}
                    </button>
                    {dlMsg && <div className="ex-small mt-3">{dlMsg}</div>}
                    <p className="ex-tiny mt-5 max-w-md mx-auto">Open the .expert file with the Experts app on your phone. Each link lasts five minutes; ask for a new one here or in My library at any time — ownership never expires.</p>
                  </div>
                )}

                {failed && (
                  <div className="ex-panel p-7 mt-10">
                    <div className="text-white text-[17px]">{job.progress || "This training couldn't be completed."}</div>
                    <Link to="/train" className="ex-btn ex-btn-secondary ex-btn-sm mt-6">Train another subject</Link>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
